import { AbstractControl } from '@angular/forms';
import { TypedDictionary } from '@core/utils';
import { isInternational } from '@shared/booker/utils/warnings-check';
import { RecentAirport } from '@shared/types';
import { getControlVal } from '@shared/ui/utils/form-control-utils/get-control-val';
import { isCityPairRestricted } from '@store/origins/api';
import { Airport } from '@store/origins/types';
import { toUpper } from 'ramda';
import { combineLatest, merge, Observable, Subscription } from 'rxjs';
import { distinctUntilChanged, filter, map, startWith } from 'rxjs/operators';

import { setCitySelctorValidation } from '../city-selector/utils/city-selector.utils';
import { CITY_PAIR_CONFIG } from './types/city-pair-selector.type';

/**
 * Function to check whether the city pairs is valid or not.
 * @param currentFormControl { FormControl } form control user is currently editing
 * @param oppositeFormControl { FormControl } form control that is in opposite direction
 * @returns { Subscription }
 */
export function validateCityPairs(
  currentFormControl: AbstractControl,
  oppositeFormControl: AbstractControl,
): Subscription {
  return getControlVal(currentFormControl)
    .pipe(
      startWith(currentFormControl.value),
      filter(Boolean),
      distinctUntilChanged(),
    )
    .subscribe((currentAirport: Airport) => {
      if (
        isCityPairRestricted(
          currentAirport.code,
          (oppositeFormControl.value || {}).code,
        )
      ) {
        setCityPairControlValue(oppositeFormControl, undefined);
      }
    });
}

export function reverseOriginAndDestination(
  fromAirport: AbstractControl,
  toAirport: AbstractControl,
): void {
  const destination = toAirport.value;
  const origin = fromAirport.value;
  // change to undefined first so would not run into city pair restricted situation
  setCityPairControlValue(fromAirport, undefined);
  setCityPairControlValue(toAirport, undefined);
  setCityPairControlValue(toAirport, origin);
  setCityPairControlValue(fromAirport, destination);
}

export function setCityPairControlValue(
  control: AbstractControl,
  val: Airport,
) {
  control.setValue(val);
  control.updateValueAndValidity();
}

export function setCityPairSelectorValidation(
  control1: AbstractControl,
  control2: AbstractControl,
  message: string,
): void {
  setCitySelctorValidation(control1, message);
  setCitySelctorValidation(control2, message);
}

export function prioritizedPrePopulatedOrigin(
  airports: TypedDictionary<Airport>,
  baseAirport: string,
  nearbyAirports: string[],
  recentAirports: RecentAirport[],
  urlAirport: string,
): Airport {
  switch (true) {
    // url airport
    case !!urlAirport:
      return airports[toUpper(urlAirport)];
    // recent search
    case !!recentAirports &&
      !!recentAirports.length &&
      !!airports[recentAirports[0].originCode[0]]:
      return airports[recentAirports[0].originCode[0]];
    // true blue preferred airport
    case !!baseAirport && !!airports[baseAirport]:
      return airports[baseAirport];
    // code of the nearest airport or MAC area
    case !!nearbyAirports &&
      !!nearbyAirports.length &&
      !!(
        getMacAreaForAirport(nearbyAirports[0], airports) ||
        airports[nearbyAirports[0]]
      ):
      return (
        getMacAreaForAirport(nearbyAirports[0], airports) ||
        airports[nearbyAirports[0]]
      );
    // Default to NYC
    default:
      return airports[CITY_PAIR_CONFIG.DEFAULT_PAIR_VALUES.FROM_AIRPORT];
  }
}

export function prioritizedPrePopulatedDestination(
  airports: TypedDictionary<Airport>,
  recentAirports: RecentAirport[],
  urlAirport: string,
): Airport {
  switch (true) {
    // url airport
    case !!urlAirport:
      return airports[toUpper(urlAirport)];
    // recent search
    case !!recentAirports && !!recentAirports.length:
      return airports[recentAirports[0].destinationCode[0]];
    default:
      return undefined;
  }
}

export function isSeasonalRoute(
  fromAirport: AbstractControl,
  toAirport: AbstractControl,
): Observable<boolean> {
  return merge(getControlVal(fromAirport), getControlVal(toAirport)).pipe(
    map((airport: Airport) => !!airport && !!airport.seasonal),
  );
}

export const areAnyAirportsSeasonalRoutes = (
  ...airports: Airport[]
): boolean => {
  return airports.some(airport => airport?.seasonal);
};

export function isInterlineRoute(
  fromAirport: AbstractControl,
  toAirport: AbstractControl,
): Observable<boolean> {
  return combineLatest([
    getControlVal(fromAirport),
    getControlVal(toAirport),
  ]).pipe(
    map(([fromAirportVal, toAirportVal]) => {
      return (
        (!!fromAirportVal && !!fromAirportVal.interline) ||
        (!!toAirportVal && !!toAirportVal.interline)
      );
    }),
  );
}

export function isInternationalRoute(
  fromAirport: AbstractControl,
  toAirport: AbstractControl,
): Observable<boolean> {
  return combineLatest([
    getControlVal(fromAirport),
    getControlVal(toAirport),
  ]).pipe(
    map(([from, to]) => {
      if (!!from && !!to) {
        return isInternational(from, to);
      } else {
        return false;
      }
    }),
  );
}

export function isSharedMarket(
  fromAirport: AbstractControl,
  toAirport: AbstractControl,
): Observable<boolean> {
  return merge(getControlVal(fromAirport), getControlVal(toAirport)).pipe(
    map((airport: Airport) => !!airport && !!airport.interline && !!airport.jb),
  );
}

export function getMacAreaForAirport(
  airportCode: string,
  airports: TypedDictionary<Airport>,
): Airport {
  const airport = airports[airportCode];
  return !!airport && airports[airport.pm];
}
